import { memo, PropsWithChildren, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSubContext } from '../dropdown.context';
import { PortalContent } from '../dropdown.styled';
import { Side, Align, PortalMode } from '../dropdown.types';
import { useDropdownPosition } from '../use-dropdown-position';

type SubContentProps = {
  side?: Side;
  align?: Align;
  mode?: PortalMode;
};

export const SubContent = memo(
  ({
    children,
    side = 'right',
    align = 'start',
    mode = 'portal',
  }: PropsWithChildren<SubContentProps>) => {
    const { open, triggerRef, close } = useSubContext();
    const contentRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState<boolean>(false);
    const [closing, setClosing] = useState<boolean>(false);

    useEffect(() => {
      if (open) {
        setVisible(true);
        setClosing(false);
      } else if (visible) {
        setClosing(true);
        const t = setTimeout(() => {
          setVisible(false);
          setClosing(false);
        }, 150);

        return () => clearTimeout(t);
      }
    }, [open, visible]);

    const pos = useDropdownPosition(
      triggerRef,
      contentRef,
      side,
      align,
      visible,
      mode === 'portal'
    );

    if (!visible) {
      return null;
    }

    const content = (
      <PortalContent
        ref={contentRef}
        data-state={closing ? 'closed' : 'open'}
        data-side={side}
        role="menu"
        tabIndex={-1}
        style={{ ...pos, zIndex: 1000 }}
        onMouseLeave={close}
      >
        {children}
      </PortalContent>
    );

    return mode === 'portal' ? createPortal(content, document.body) : content;
  }
);

SubContent.displayName = 'Dropdown.SubContent';
